import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 30;

const statusClassMap = {
  sent: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  skipped: 'bg-amber-100 text-amber-700 border-amber-200',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const AdminGatewayLogs = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [gatewayOptions, setGatewayOptions] = useState([]);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    gateway: 'all',
    recipient_type: 'all',
    date_from: '',
    date_to: '',
  });

  const fetchLogs = async (withRefreshSpinner = false) => {
    if (withRefreshSpinner) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await axios.get(`${API}/payments/gateway-logs`, {
        params: {
          ...filters,
          limit: PAGE_SIZE,
          skip,
        },
      });

      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setGatewayOptions(response.data.gateway_options || []);
    } catch (error) {
      toast({
        title: 'Failed to load gateway logs',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, filters.status, filters.gateway, filters.recipient_type, filters.date_from, filters.date_to]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLogs(false);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const totalsByStatus = useMemo(() => {
    return logs.reduce(
      (acc, log) => {
        const key = (log.status || 'unknown').toLowerCase();
        if (key === 'sent') acc.sent += 1;
        else if (key === 'failed') acc.failed += 1;
        else if (key === 'skipped') acc.skipped += 1;
        return acc;
      },
      { sent: 0, failed: 0, skipped: 0 },
    );
  }, [logs]);

  const updateFilter = (key, value) => {
    setSkip(0);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" data-testid="admin-gateway-logs-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="gateway-logs-title">
            <Mail className="w-6 h-6 text-[#6e2ea8]" />
            Gateway Email Logs
          </h1>
          <p className="text-gray-500 mt-1" data-testid="gateway-logs-subtitle">
            Per-order customer/admin email delivery logs with payment + shipping gateway context.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/admin/settings/payments" data-testid="gateway-logs-payments-link" className="inline-flex">
            <Button variant="outline">Payment Settings</Button>
          </Link>
          <Link to="/admin/settings/shipping" data-testid="gateway-logs-shipping-link" className="inline-flex">
            <Button variant="outline">Shipping Settings</Button>
          </Link>
          <Button
            onClick={() => fetchLogs(true)}
            className="bg-[#6e2ea8] hover:bg-[#5a238c]"
            data-testid="gateway-logs-refresh-button"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <Card data-testid="gateway-logs-summary-card">
        <CardHeader>
          <CardTitle>Current Page Summary</CardTitle>
          <CardDescription>Total matching logs: {total}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Badge className="bg-green-100 text-green-700 border border-green-200" data-testid="gateway-logs-summary-sent">Sent: {totalsByStatus.sent}</Badge>
          <Badge className="bg-red-100 text-red-700 border border-red-200" data-testid="gateway-logs-summary-failed">Failed: {totalsByStatus.failed}</Badge>
          <Badge className="bg-amber-100 text-amber-700 border border-amber-200" data-testid="gateway-logs-summary-skipped">Skipped: {totalsByStatus.skipped}</Badge>
        </CardContent>
      </Card>

      <Card data-testid="gateway-logs-filters-card">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gateway-log-search">Search</Label>
            <Input
              id="gateway-log-search"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder="Order #, email, message"
              data-testid="gateway-logs-search-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
              <SelectTrigger data-testid="gateway-logs-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Gateway</Label>
            <Select value={filters.gateway} onValueChange={(value) => updateFilter('gateway', value)}>
              <SelectTrigger data-testid="gateway-logs-gateway-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All gateways</SelectItem>
                {gatewayOptions.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Recipient</Label>
            <Select value={filters.recipient_type} onValueChange={(value) => updateFilter('recipient_type', value)}>
              <SelectTrigger data-testid="gateway-logs-recipient-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recipients</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gateway-log-date-from">From</Label>
            <Input id="gateway-log-date-from" type="date" value={filters.date_from} onChange={(event) => updateFilter('date_from', event.target.value)} data-testid="gateway-logs-date-from-input" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gateway-log-date-to">To</Label>
            <Input id="gateway-log-date-to" type="date" value={filters.date_to} onChange={(event) => updateFilter('date_to', event.target.value)} data-testid="gateway-logs-date-to-input" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="gateway-logs-table-card">
        <CardContent className="pt-6">
          {loading ? (
            <div className="h-48 flex items-center justify-center" data-testid="gateway-logs-loading-state">
              <Loader2 className="w-7 h-7 animate-spin text-[#6e2ea8]" />
            </div>
          ) : logs.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500" data-testid="gateway-logs-empty-state">
              No gateway logs found for the selected filters.
            </div>
          ) : (
            <div className="overflow-auto" data-testid="gateway-logs-table-wrapper">
              <table className="min-w-full text-sm" data-testid="gateway-logs-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3">Time</th>
                    <th className="text-left p-3">Order</th>
                    <th className="text-left p-3">Gateway</th>
                    <th className="text-left p-3">Recipient</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-t" data-testid={`gateway-log-row-${log.id}`}>
                      <td className="p-3 text-gray-600" data-testid={`gateway-log-time-${log.id}`}>{formatDateTime(log.created_at)}</td>
                      <td className="p-3" data-testid={`gateway-log-order-${log.id}`}>
                        <div className="font-medium text-gray-900">{log.order_number || '—'}</div>
                        <div className="text-xs text-gray-500">{log.customer_name || log.customer_email || 'Unknown customer'}</div>
                      </td>
                      <td className="p-3" data-testid={`gateway-log-gateway-${log.id}`}>
                        <div className="font-medium uppercase text-gray-800">{log.gateway || 'unknown'}</div>
                        <div className="text-xs text-gray-500">Shipping: {log.shipping_gateway || 'unknown'}</div>
                      </td>
                      <td className="p-3" data-testid={`gateway-log-recipient-${log.id}`}>
                        <div className="capitalize text-gray-800">{log.recipient_type || 'unknown'}</div>
                        <div className="text-xs text-gray-500">{log.to_email || '—'}</div>
                      </td>
                      <td className="p-3" data-testid={`gateway-log-status-${log.id}`}>
                        <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold capitalize ${statusClassMap[log.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {log.status || 'unknown'}
                        </span>
                      </td>
                      <td className="p-3 text-gray-700" data-testid={`gateway-log-message-${log.id}`}>
                        <div>{log.message || '—'}</div>
                        {log.error ? <div className="text-xs text-red-600 mt-1">{log.error}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4" data-testid="gateway-logs-pagination-controls">
            <p className="text-xs text-gray-500" data-testid="gateway-logs-pagination-text">
              Showing {logs.length === 0 ? 0 : skip + 1}–{Math.min(skip + logs.length, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setSkip((prev) => Math.max(0, prev - PAGE_SIZE))} disabled={skip === 0} data-testid="gateway-logs-prev-page-button">
                Previous
              </Button>
              <Button variant="outline" onClick={() => setSkip((prev) => prev + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total} data-testid="gateway-logs-next-page-button">
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGatewayLogs;
